import { VALIDATION_RESULT_ITEM_CODE, VALIDATION_RESULT_ITEM_TYPE } from "../types/enums";
import { ENVIRONMENT, MINIMAL_BACKEND_VERSION, TOAST_VERSION_KEY } from "../constants/constants";
import type { Household, ValidationResultItem } from "@/types/types";
import { type IPublicClientApplication } from "@azure/msal-browser";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { Id, toast } from "react-toastify";
import clsx, { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDate } from "devextreme/localization";

export const isMobile = {
  Android: function () {
    return navigator.userAgent.match(/Android/i);
  },
  BlackBerry: function () {
    return navigator.userAgent.match(/BlackBerry/i);
  },
  iOS: function () {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i);
  },
  Opera: function () {
    return navigator.userAgent.match(/Opera Mini/i);
  },
  Windows: function () {
    return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i);
  },
  any: function () {
    return (
      isMobile.Android() ||
      isMobile.BlackBerry() ||
      isMobile.iOS() ||
      isMobile.Opera() ||
      isMobile.Windows()
    );
  },
};

export default function errorNotifications(errors: ValidationResultItem[]) {
  errors.forEach((item) => {
    if (item.type === VALIDATION_RESULT_ITEM_TYPE.Error) {
      toast.error(item.message, {
        autoClose: 5000,
        toastId: `${item.code}-${item.type}-${item.message}`,
      });
    } else if (item.type === VALIDATION_RESULT_ITEM_TYPE.Warning) {
      if (item.property === "StockPriceRegister") return;
      toast.warn(item.message, {
        autoClose: 5000,
        toastId: `${item.code}-${item.type}-${item.message}`,
      });
    } else if (item.type === VALIDATION_RESULT_ITEM_TYPE.Notice) {
      if (item.code === VALIDATION_RESULT_ITEM_CODE.Success) {
        toast.success(item.message, {
          autoClose: 3500,
          toastId: `${item.code}-${item.type}-${item.message}`,
        });
        return;
      }
      toast.info(item.message, {
        autoClose: 3500,
        toastId: `${item.code}-${item.type}-${item.message}`,
      });
    }
  });
}

export function serverError(errorTarget: string) {
  toast.error("Critical error: " + errorTarget, { autoClose: false });
}

export const NAV_ITEM_CLASSES = ({ isActive }: { isActive: boolean }) =>
  `${isActive ? "bg-white text-primary " : "text-white"} py-4 px-8 decoration-0 uppercase`;

export const getYesterdayDate = () => {
  const date = new Date();
  return new Date(date.setDate(date.getDate() - 1));
};

export function signOutClickHandler(instance: IPublicClientApplication, userId: string) {
  const logoutRequest = {
    account: instance.getAccountByHomeId(userId),
    postLogoutRedirectUri: "/",
  };
  instance.logoutRedirect(logoutRequest);
}

export const adjustSortArrowPosition = (gridRef: React.MutableRefObject<DataGridRef | null>) => {
  const gridInstance = gridRef?.current?.instance;
  if (!gridInstance) return;

  const element = gridInstance().element().querySelector(".dx-sort") as HTMLElement;

  if (element) {
    element.style.marginTop = "42%";
  }
};

export const getOwnerName = (
  household: Household | Partial<Household> | undefined,
  clientExternalCode: string | undefined | null,
  fullName = false
) => {
  const primaryContact = household?.primaryContact;
  const secondaryContact = household?.secondaryContact;

  if (household?.isSingle) {
    if (fullName) {
      return primaryContact?.preferredFullName ?? "No owner found";
    } else {
      return primaryContact?.preferredFirstName ?? "No owner found";
    }
  }

  if (!primaryContact && !secondaryContact) {
    return "No owner found";
  }

  const ownerName =
    primaryContact?.externalCode === clientExternalCode
      ? fullName
        ? primaryContact?.preferredFullName
        : primaryContact?.preferredFirstName
      : fullName
        ? secondaryContact?.preferredFullName
        : secondaryContact?.preferredFirstName;

  return ownerName ?? "No owner found";
};

export const checkClientsBirthdates = (household: Household | undefined | Partial<Household>) => {
  const primaryContactBirthDate = household?.primaryContact?.birthDate;
  const secondaryContactBirthDate = household?.secondaryContact?.birthDate;

  return !primaryContactBirthDate || (household?.secondaryContact && !secondaryContactBirthDate);
};

export const updateToast = (
  toastId: Id,
  type: "info" | "success" | "error",
  message: string,
  autoClose = 3000
) => {
  toast.update(toastId, { type, render: message, isLoading: false, autoClose });
};

export const deepCompare = <T extends object>(
  obj1: T | undefined,
  obj2: T | undefined
): boolean => {
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1) as (keyof T)[];
  const keys2 = Object.keys(obj2) as (keyof T)[];

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects =
      typeof val1 === "object" && typeof val2 === "object" && val1 !== null && val2 !== null;
    if ((areObjects && !deepCompare(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
};

export function base64ToFile(base64String: string | undefined, mimeType: string = "") {
  if (!base64String) return null;

  const matches = base64String.match(/^data:(.+);base64,(.*)$/);
  if (matches && !mimeType) {
    mimeType = matches[1] || "";
  }

  const base64Data = base64String.replace(/^data:.+;base64,/, "");
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export const getBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const shouldDisplayToastVersion = () => {
  const obj = localStorage.getItem(TOAST_VERSION_KEY);
  if (!obj) return true;
  const lastDisplay = obj ? JSON.parse(obj).date : null;
  const minimalVer = obj ? JSON.parse(obj).version : null;
  const lastDisplayTime = new Date(lastDisplay);
  const currentTime = new Date();
  const hoursDiff = Math.abs(currentTime.getTime() - lastDisplayTime.getTime()) / 36e5;
  return hoursDiff >= 24 || minimalVer !== MINIMAL_BACKEND_VERSION;
};

export const updateLastToastDisplay = () => {
  localStorage.setItem(
    TOAST_VERSION_KEY,
    JSON.stringify({ date: new Date().toISOString(), version: MINIMAL_BACKEND_VERSION })
  );
};

export function compareBackendVersions(currentVersion: string) {
  const currentVersionArray = currentVersion.split(".").map(Number);
  const minimalVersionArray = MINIMAL_BACKEND_VERSION.split(".").map(Number);

  const [currentMajor, currentMinor, currentPatch] = currentVersionArray;
  const [minimalMajor, minimalMinor, minimalPatch] = minimalVersionArray;

  if (currentMajor < minimalMajor) {
    return {
      canRun: false,
      showError: true,
      message: `API major version mismatch (current: ${currentVersion}, minimal: ${MINIMAL_BACKEND_VERSION}): Backend update required. Please contact your administrator.`,
      warn: false,
    };
  }

  if (currentMajor === minimalMajor && currentMinor < minimalMinor) {
    return {
      canRun: true,
      showError: false,
      message: `API minor version mismatch (current: ${currentVersion}, minimal: ${MINIMAL_BACKEND_VERSION}): Some features may not work correctly.`,
      warn: true,
    };
  }

  if (
    currentMajor === minimalMajor &&
    currentMinor === minimalMinor &&
    currentPatch < minimalPatch
  ) {
    return {
      canRun: true,
      showError: false,
      message: `API patch version mismatch (current: ${currentVersion}, minimal: ${MINIMAL_BACKEND_VERSION}): It's recommended to update the backend.`,
      warn: true,
    };
  }

  return {
    canRun: true,
    showError: false,
    message: "Versions match: No issues detected.",
    warn: false,
  };
}

export const devLog = (...args: unknown[]) => {
  if (ENVIRONMENT === "DEV") {
    console.log("DEV LOG:", ...args);
  }
};

export const semverGreaterThan = (versionA: string, versionB: string) => {
  const versionsA = versionA.split(/\./g);
  const versionsB = versionB.split(/\./g);
  while (versionsA.length || versionsB.length) {
    const a = Number(versionsA.shift());
    const b = Number(versionsB.shift());
    if (a === b) continue;
    return a > b || isNaN(b);
  }
  return false;
};

export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor = 500
): ((...args: Parameters<F>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<F>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getHouseholdPrintFileName({
  clientCode,
  tabName,
}: {
  clientCode?: string;
  tabName: string;
}) {
  return `${tabName} ${formatDate(new Date(), "MM-dd-yyyy")} ${clientCode}.pdf`;
}

export function hexToHSL(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h = (h ?? 0) / 6;
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToCSS(h: number, s: number, l: number) {
  return `hsl(${h}, ${s}%, ${l}%)`;
}
